import React from 'react';

import { tv } from '../../tv';
import { Size } from '../../types';

/**
 * A number and the band it falls in.
 *
 * The number itself is NOT coloured. That is the settled position across the
 * systems that ship a metric: Chakra v3's `Stat.ValueText` takes no props at
 * all and every one of its examples leaves the value uncoloured, putting the
 * colour on an adjacent badge. Primer removed the colour prop from `Text`,
 * and Radix refuses to give text semantic colour names.
 *
 * The reason is accessibility rather than taste. A bare coloured "82/100"
 * carries its band in colour alone, which is WCAG 1.4.1 (the same shape as
 * F81). The digits do not rescue it: nothing on screen says whether high is
 * good or bad. WCAG explicitly withdraws the luminance exemption for this
 * case, naming red-for-invalid against green-for-valid.
 *
 * So the band is a LABELLED badge. The colour becomes redundant
 * reinforcement of a label that already says "Critical", which is what
 * Polaris enforces by rendering a visually-hidden label inside its own Badge.
 *
 * A `tone` prop on Text was considered and rejected. It is un-guardrailed: it
 * lets a caller match the design while silently dropping the label, because
 * nothing forces the second channel. Deriving the badge from the bands means
 * a colour cannot be rendered without the word that explains it.
 */

const scoreStyles = tv({
  slots: {
    root: 'ck:flex ck:items-baseline ck:gap-3 ck:flex-wrap',
    /* A score is a display figure rather than body text, so it reads on the
     * heading steps: capping it at the body scale would shrink a headline
     * number to the size of the prose around it. */
    value: 'ck:font-bold ck:text-text-default',
    badge: [
      'ck:inline-flex ck:items-center ck:rounded-md ck:border',
      'ck:px-2 ck:py-1 ck:text-xs ck:font-semibold',
    ],
  },
  variants: {
    size: {
      sm: { value: 'ck:text-lg' },
      md: { value: 'ck:text-2xl' },
      lg: { value: 'ck:text-3xl' },
    },
    /* Named for meaning, not hue, so the palette can move without touching a
     * call site. Each sits on its scale's step 11 for text and step 3 for the
     * fill, the pairing Radix designs for a low-contrast chip. */
    tone: {
      neutral: { badge: 'ck:bg-neutral-3 ck:text-text-default ck:border-neutral-6' },
      success: { badge: 'ck:bg-green-3 ck:text-success-text ck:border-green-6' },
      warning: { badge: 'ck:bg-orange-3 ck:text-warning-text ck:border-orange-6' },
      critical: { badge: 'ck:bg-red-3 ck:text-error-text ck:border-red-6' },
    },
  },
  defaultVariants: { tone: 'neutral', size: 'md' },
});

export type ScoreTone = 'neutral' | 'success' | 'warning' | 'critical';

export interface ScoreBand {
  /** Inclusive upper bound of this band. */
  max: number;
  /** The word shown to the reader. This is what makes the colour redundant. */
  label: string;
  tone: ScoreTone;
}

export interface ScoreProps {
  /** The value shown, rendered as given. */
  value: number;
  /** How prominent the figure is. A score is display type, not body text. */
  size?: Size;
  /** Optional denominator, so a score reads "82/100". */
  outOf?: number;
  /**
   * Bands in ascending order, each with the label its colour stands for.
   * One table rather than parallel colour and label tables, which is what
   * lets the two disagree.
   */
  bands: readonly ScoreBand[];
  /**
   * Whether a HIGHER value is better. Most compliance metrics are the other
   * way round, and a system that assumes up-is-good bakes in the wrong
   * polarity: Chakra v2 hardcodes green for an upward arrow. Bands carry
   * their own tone, so this only labels the direction for assistive
   * technology rather than choosing a colour.
   */
  isHigherBetter?: boolean;
  /** Names what is being scored, for assistive technology. */
  label?: string;
}

function bandFor(value: number, bands: readonly ScoreBand[]): ScoreBand | undefined {
  return bands.find((b) => value <= b.max) ?? bands[bands.length - 1];
}

export const Score = React.forwardRef<HTMLDivElement, ScoreProps>(function Score(
  { value, outOf, bands, size = 'md', isHigherBetter = false, label },
  ref,
) {
  const band = bandFor(value, bands);
  const { root, value: valueClass, badge } = scoreStyles({ tone: band?.tone, size });
  const shown = outOf === undefined ? `${value}` : `${value}/${outOf}`;

  return (
    <div
      ref={ref}
      className={root()}
      data-component="score"
      /* The band word is beside the number, so a screen reader gets the
       * judgement without depending on the colour. */
      aria-label={
        label && band
          ? `${label}: ${shown}, ${band.label} (${isHigherBetter ? 'higher' : 'lower'} is better)`
          : undefined
      }
    >
      <span className={valueClass()}>{shown}</span>
      {band && <span className={badge()}>{band.label}</span>}
    </div>
  );
});
